import React, { useState, useCallback } from 'react';
import LeftSidebar from '../components/LeftSidebar';
import RightSidebar from '../components/RightSidebar';
import MediaUpload from '../components/MediaUpload';
import VideoPlayer from '../components/VideoPlayer';
import ChatWithAI from '../components/ChatWithAI';
import TimeLiner from '../components/Timeliner';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import '../styles/VideoEditorPage.css';
import { baseUrl } from '../config';



function VideoEditorPage() {
  // State management
  const [mediaFile, setMediaFile] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [textOverlays, setTextOverlays] = useState([]);
  const [brandSettings, setBrandSettings] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [videoEdits, setVideoEdits] = useState({
    trim: { start: 0, end: 0 },
    filters: [],
    transitions: [],
    text: []
  });

  // Handle media upload
  const handleMediaUpload = useCallback((file) => {
    setMediaFile(file);
    setVideoEdits({
      trim: { start: 0, end: file.duration || 0 },
      filters: [],
      transitions: [],
      text: []
    });
  }, []);

  // Handle chat interactions
  const handleChatOpen = () => setIsChatOpen(true);
  const handleChatClose = () => setIsChatOpen(false);

  // Handle settings changes
  const handleSettingsChange = useCallback((newSettings) => {
    setSettings(newSettings);
    if (mediaFile) {
      setVideoEdits(prev => ({
        ...prev,
        filters: [...prev.filters, newSettings.filter]
      }));
    }
  }, [mediaFile]);

  // Handle option selection from LeftSidebar
  const handleOptionSelect = useCallback((option, data) => {
    switch (option) {
      case 'Templates':
        setActiveTemplate(data);
        if (data && mediaFile) {
          setVideoEdits(prev => ({
            ...prev,
            ...data.defaultSettings
          }));
        }
        break;

      case 'Text':
        if (data) {
          setTextOverlays(prev => [...prev, data]);
          setVideoEdits(prev => ({
            ...prev,
            text: [...prev.text, data]
          }));
        }
        break;

      case 'Transitions':
        setSelectedTransition(data);
        if (data) {
          setVideoEdits(prev => ({
            ...prev,
            transitions: [...prev.transitions, data]
          }));
        }
        break;

      case 'Brand kit':
        setBrandSettings(data);
        if (data) {
          setVideoEdits(prev => ({
            ...prev,
            brandKit: data
          }));
        }
        break;

      default:
        break;
    }
  }, [mediaFile]);

  // Handle video trimming
  const handleTrim = useCallback((trimData) => {
    setVideoEdits(prev => ({
      ...prev,
      trim: trimData
    }));
  }, []);

  // Handle timeline updates
  const handleTimelineUpdate = useCallback((timelineData) => {
    setVideoEdits(prev => ({
      ...prev,
      ...timelineData
    }));
  }, []);

  // Handle video export
  const handleExport = useCallback(async () => {
    if (!mediaFile) return;
    
    setIsExporting(true);
    try {
      // Implement your export logic here
      // This is a placeholder for actual export functionality
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
    } finally {
      setIsExporting(false);
    }
  }, [mediaFile, videoEdits]);

  return (
    <div className="video-editor-page">
      <SEO
        title="Video Editor"
        description="Edit your film's video content with ProducerBuddy's integrated video editor tool."
        url="https://producerbuddy.pilomation.com/video-editor"
        robots="noindex, nofollow"
      />
      <SubNav />
      <div className="editor-container">
        <LeftSidebar 
          onMediaUpload={handleMediaUpload} 
          onChatOpen={handleChatOpen} 
          onOptionSelect={handleOptionSelect}
        />
        
        {isChatOpen && (
          <ChatWithAI 
            isOpen={isChatOpen} 
            onClose={handleChatClose}
            onSuggestion={(suggestion) => handleOptionSelect('Text', suggestion)} 
          />
        )}
        
        <div className="editor-main">
          {mediaFile ? (
            <>
              <div className="video-player-container">
                <VideoPlayer 
                  mediaFile={mediaFile} 
                  edits={videoEdits}
                  textOverlays={textOverlays}
                  onTrim={handleTrim}
                  settings={settings}
                />
                <div className="video-controls">
                  {/* Add your video control buttons here */}
                </div>
              </div>
              
              <div className="timeline-container">
                <TimeLiner 
                  duration={mediaFile.duration}
                  edits={videoEdits}
                  onUpdate={handleTimelineUpdate}
                  transitions={selectedTransition ? [selectedTransition] : []}
                  textOverlays={textOverlays}
                />
              </div>
              
              <button 
                className="export-button"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Video'}
              </button>
            </>
          ) : (
            <div className="media-upload-container">
              <MediaUpload 
                onMediaUpload={handleMediaUpload} 
                acceptedTypes={['video/*']}
              />
            </div>
          )}
        </div>
        
        <RightSidebar 
          onSettingsChange={handleSettingsChange}
          currentEdits={videoEdits}
          mediaFile={mediaFile}
          template={activeTemplate}
          brandSettings={brandSettings}
        />
      </div>
    </div>
  );
}

export default VideoEditorPage;
